import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, Briefcase, Calendar, Inbox, User, TrendingUp, Building2, ChevronRight, ChevronDown, Plus, ChevronsLeft, ChevronsRight, Settings, HelpCircle } from 'lucide-react';

import Button from '../../../components/ui/Button';
import Tooltip from '../../../components/ui/Tooltip';
import { useRoleAccess } from '../../../hooks/useRoleAccess';
import { hasFeatureAccess } from '../../../utils/roleValidation';
import { useMobileResponsive, useSwipeGesture } from '../../../hooks/useMobileResponsive';

const DashboardSidebar = ({ activeTab, onTabChange }) => {
  const [jobsExpanded, setJobsExpanded] = useState(true);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { userRole } = useRoleAccess();
  const location = useLocation();
  const { isMobile } = useMobileResponsive();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Swipe gesture handlers for mobile
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => {
      if (isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    },
    onSwipeRight: () => {
      if (isMobile && !mobileOpen) {
        setMobileOpen(true);
      }
    }
  });

  // Close mobile sidebar when route changes
  React.useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [location?.pathname]);

  // Mock company data
  const mockCompanies = [
    { id: 1, name: 'Google', logo: 'G', role: 'Software Engineer' },
    { id: 2, name: 'Microsoft', logo: 'M', role: 'Senior Developer' }
  ];

  const sidebarClasses = "fixed left-0 top-16 bottom-0 bg-background border-r border-border z-30 overflow-hidden";

  const sidebarVariants = {
    expanded: {
      width: 240,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    },
    collapsed: {
      width: 64,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    }
  };

  const contentVariants = {
    expanded: {
      opacity: 1,
      transition: { delay: 0.1 }
    },
    collapsed: {
      opacity: 0,
      transition: { duration: 0.1 }
    }
  };

  const jobSeekerMenu = [
    {
      id: 'dashboard',
      icon: LayoutGrid,
      label: 'Dashboard Home',
      path: '/dashboard',
      exact: true
    },
    {
      id: 'jobs',
      icon: Briefcase,
      label: 'Jobs',
      path: '/dashboard/jobs'
    },
    {
      id: 'interviews',
      icon: Calendar,
      label: 'Interviews & Calendar',
      path: '/dashboard/interviews'
    },
    {
      id: 'inbox',
      icon: Inbox,
      label: 'Inbox',
      path: '/dashboard/inbox',
      badge: 8
    },
    {
      id: 'profile',
      icon: User,
      label: 'Profile & Settings',
      path: '/dashboard/profile'
    },
    {
      id: 'insights',
      icon: TrendingUp,
      label: 'Career Insights',
      path: '/dashboard/insights'
    },
    {
      id: 'support',
      icon: HelpCircle,
      label: 'Help & Support',
      path: '/support-ticket-management-dashboard'
    }
  ];

  // Filter menu items based on role permissions
  const filteredMenuItems = jobSeekerMenu?.filter(item => {
    // Map menu paths to feature permissions
    const featureMap = {
      '/dashboard/jobs': 'view_applications',
      '/dashboard/saved-jobs': 'save_jobs',
      '/dashboard/applications': 'view_applications',
      '/dashboard/profile': 'manage_profile'
    };

    const feature = featureMap?.[item?.path];
    
    // If no feature mapping, show by default
    if (!feature) return true;
    
    // Check if user has access to this feature
    return hasFeatureAccess(userRole, feature);
  });

  const renderMenuItem = (item) => {
    return (
      <div key={item?.id} className="relative">
        <NavLink
          to={item?.path}
          end={item?.exact}
          className={({ isActive }) =>
            `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${
              isActive
                ? 'bg-accent/10 text-accent' :'text-muted-foreground hover:bg-accent/10 hover:text-accent'
            }`
          }
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                variants={contentVariants}
                initial="collapsed"
                animate="expanded"
                exit="collapsed"
                className="truncate flex-1"
              >
                {item?.label}
              </motion.span>
            )}
          </AnimatePresence>
          
          {/* Badge */}
          {item?.badge && !collapsed && (
            <motion.span
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {item?.badge}
            </motion.span>
          )}
        </NavLink>
        {collapsed && (
          <Tooltip content={item?.label} side="right">
            <div className="absolute inset-0" />
          </Tooltip>
        )}
      </div>
    );
  };

  const onToggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <>
      {/* Mobile Backdrop */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={onToggle}
            aria-label="Close sidebar"
          />
        )}
      </AnimatePresence>
      {/* Sidebar */}
      <motion.aside
        {...swipeHandlers}
        initial={false}
        animate={collapsed ? "collapsed" : "expanded"}
        variants={sidebarVariants}
        className={`fixed left-0 top-16 bottom-0 bg-background border-r border-border z-30 overflow-hidden ${
          isMobile ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') : ''
        } transition-transform duration-300`}
      >
      <div className="h-full flex flex-col">
        {/* Toggle Button - Desktop Only */}
        <div className="hidden lg:flex items-center justify-end p-3 border-b border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronsRight className="w-4 h-4" />
            ) : (
              <ChevronsLeft className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-1">
            {filteredMenuItems?.map((item) => (
              <li key={item?.label}>
                {renderMenuItem(item)}
              </li>
            ))}
          </ul>
        </nav>

        {/* Company Switcher Section */}
        <div className="border-t border-border p-4 space-y-2">
          {/* Switch to Employer Button */}
          <div className="relative">
            <button
              onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                collapsed ? 'justify-center' : ''
              } text-muted-foreground hover:bg-accent/10 hover:text-accent`}
            >
              <Building2 className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    variants={contentVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    className="truncate flex-1 text-left"
                  >
                    Switch to Employer
                  </motion.span>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    variants={contentVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                  >
                    {companySwitcherOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            {collapsed && (
              <Tooltip content="Switch to Employer" side="right">
                <div className="absolute inset-0" />
              </Tooltip>
            )}
          </div>

          {/* Company Dropdown */}
          <AnimatePresence>
            {companySwitcherOpen && !collapsed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="ml-6 space-y-2"
              >
                {mockCompanies?.map(company => (
                  <button
                    key={company?.id}
                    onClick={() => navigate(`/employer/company/${company?.id}`)}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors text-muted-foreground hover:bg-accent/5 hover:text-accent"
                  >
                    <div className="w-6 h-6 bg-accent text-accent-foreground rounded text-xs font-semibold flex items-center justify-center">
                      {company?.logo}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium">{company?.name}</div>
                      <div className="text-xs text-muted-foreground">{company?.role}</div>
                    </div>
                  </button>
                ))}

                {/* Create Company Button */}
                <button
                  onClick={() => navigate('/employer/create-company')}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors text-muted-foreground hover:bg-accent/5 hover:text-accent"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create Company Page</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.aside>
    </>
  );
};

export default DashboardSidebar;